import React, { lazy, Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { translate } from '@utils/i18n';
import { cn } from '@utils/tailwind-class.merge';
import { useAlertsDrawer } from '_clean-code/contexts/AlertsDrawerContext';
import { HEADER_HEIGHT_PX } from 'layouts/AppLayout/constants';
// Code-split: panel content is only fetched the first time the panel opens.
const AlertsSidePanelContent = lazy(() => import('./AlertsSidePanelContent'));

function PanelSkeleton() {
  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-3 tw-animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="tw-h-24 tw-rounded-md tw-bg-gray-100" />
      ))}
    </div>
  );
}

export function AlertsSidePanel() {
  const { isOpen, close } = useAlertsDrawer();

  // Deferred mount: only load + mount content after first open.
  // Once mounted, it stays mounted (preserves scroll, avoids re-fetching).
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    if (isOpen && !hasMounted) {
      setHasMounted(true);
    }
  }, [isOpen, hasMounted]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={cn(
          //
          'tw-fixed tw-inset-0 tw-bg-transparent tw-transition-opacity tw-duration-300 tw-z-[200]',
          isOpen ? 'tw-opacity-100' : 'tw-opacity-0 tw-pointer-events-none'
        )}
        onClick={close}
        aria-hidden="true"
      />

      {/* Side panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={translate('alerts_panel')}
        style={{
          top: HEADER_HEIGHT_PX,
          height: `calc(100vh - ${HEADER_HEIGHT_PX + 16}px)`
        }}
        className={cn(
          'tw-fixed tw-right-4 tw-w-[400px]',
          'tw-bg-white tw-shadow-[0px_12px_16px_0px_rgba(37,43,42,0.08),0px_4px_6px_0px_rgba(37,43,42,0.03)]',
          'tw-border tw-border-[#eaeaea] tw-rounded-md',
          'tw-transition-transform tw-duration-300 tw-ease-in-out',
          'tw-z-[201]',
          isOpen ? 'tw-translate-x-0' : 'tw-translate-x-[calc(100%+1rem)]'
        )}
      >
        {hasMounted && (
          <Suspense fallback={<PanelSkeleton />}>
            <AlertsSidePanelContent />
          </Suspense>
        )}
      </div>
    </>,
    document.body
  );
}

export default AlertsSidePanel;
